<script setup lang="ts">
import { watch, ref, computed } from 'vue'
import { FormKit } from '@formkit/vue'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import type ObjectConfig from '@/types/ObjectConfig'

const props = defineProps<{
  isOpen: boolean
  initialData: any
  headquartersTypes: ObjectConfig[]
  vhqs: any[]
}>()

const emit = defineEmits<{
  (e: 'openChange', value: boolean): void
  (e: 'save', value: any): void
}>()

const isOpen = computed({
  get: () => props.isOpen,
  set: (val: boolean) => emit('openChange', val),
})

const values = ref({
  ...props.initialData,
  selectedVhqAddressId: props.initialData?.selectedVhqAddressId ?? null,
})

function handleOpenChange(val: boolean) {
  emit('openChange', val)
}

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      values.value = { ...props.initialData }
    }
  },
)

function onSubmit() {
  emit('save', { ...values.value })
  emit('openChange', false)
}
</script>

<template>
  <Dialog :open="isOpen" @update:open="handleOpenChange">
    <DialogContent class="max-w-[calc(100%-2rem)] rounded-3xl p-6 sm:max-w-146 sm:p-10">
      <DialogHeader class="gap-2 pe-14 text-start">
        <DialogTitle>Adresa a sídlo</DialogTitle>
        <DialogDescription>Upravte adresu a sídlo spoločnosti.</DialogDescription>
      </DialogHeader>
      <form novalidate @submit="onSubmit">
        <div class="overflow-y-auto max-h-[calc(100vh-4rem)] space-y-6">
          <!-- Naše virtuálne sídlo - full width -->
          <div class="col-span-full">
            <label for="is_virtual" class="block text-sm font-medium text-gray-700 pr-2 dark:text-gray-300">
              Naše virtuálne sídlo
            </label>
            <FormKit
              type="checkbox"
              id="is_virtual"
              name="is_virtual"
              v-model="values.is_virtual"
            />
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-5 sm:gap-6">
            <!-- Typ sídla -->
            <div>
              <label for="headquarters_type" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Typ sídla
              </label>
              <FormKit
                type="select"
                id="headquarters_type"
                name="headquarters_type"
                v-model="values.headquarters_type"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              >
                <option v-for="type in headquartersTypes" :key="type.id" :value="type.id">
                  {{ type.name }}
                </option>
              </FormKit>
            </div>

            <!-- Ulica -->
            <div>
              <label for="street" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Ulica
              </label>
              <FormKit
                type="text"
                id="street"
                name="street"
                v-model="values.street"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              />
            </div>

            <!-- Súpisné číslo -->
            <div>
              <label for="street_number" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Súpisné číslo
              </label>
              <FormKit
                type="text"
                id="street_number"
                name="street_number"
                v-model="values.street_number"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              />
            </div>

            <!-- Orientačné číslo -->
            <div>
              <label for="street_number2" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Orientačné číslo
              </label>
              <FormKit
                type="text"
                id="street_number2"
                name="street_number2"
                v-model="values.street_number2"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              />
            </div>

            <!-- Mesto -->
            <div>
              <label for="city" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Mesto
              </label>
              <FormKit
                type="text"
                id="city"
                name="city"
                v-model="values.city"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              />
            </div>

            <!-- PSČ -->
            <div>
              <label for="psc" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                PSČ
              </label>
              <FormKit
                type="text"
                id="psc"
                name="psc"
                v-model="values.psc"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              />
            </div>

            <!-- Krajina -->
            <div>
              <label for="country" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Krajina
              </label>
              <FormKit
                type="text"
                id="country"
                name="country"
                v-model="values.country"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              />
            </div>

            <!-- Virtuálne sídlo (lokácia) -->
            <div v-if="values.is_virtual" class="col-span-full">
              <label for="selectedVhqAddressId" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Virtuálne sídlo (lokácia)
              </label>
              <FormKit
                type="select"
                id="selectedVhqAddressId"
                name="selectedVhqAddressId"
                v-model="values.selectedVhqAddressId"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              >
                <option v-if="!vhqs?.length">Žiadne lokácie sa nenašli</option>
                <option v-for="vhq in vhqs" :key="vhq.id" :value="vhq.id">
                  {{ vhq.name }}
                </option>
              </FormKit>
            </div>
          </div>
        </div>
        <DialogFooter class="mx-0 mb-0 gap-3 p-0 pt-2 sm:justify-end">
          <div class="flex flex-col-reverse justify-end gap-3 pt-3 sm:flex-row">
            <Button variant="outline" type="button" @click="handleOpenChange(false)">
              Zrušiť
            </Button>
            <Button type="submit">Uložiť</Button>
          </div>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>